import * as THREE from "three/webgpu";
import {SketchDrawingHandler, SketchDrawingMouseDownPayload} from './SketchDrawingHandler';
import type {SketchDrawingManager} from '../manager/SketchDrawingManager';
import {SketchDrawingTool} from './DrawingSharedTypes';
import {
    SketchBezierCurveClosureKind,
    SketchBezierCurveKind,
    type SketchBezierCurveMetadata,
    SketchBezierSelectionKind,
    SketchPointObject
} from '../mesh/sketch';
import {SketchSplineObject} from '../mesh/sketch/SketchSplineObject';
import {normalizeSplineInterpolationPoints} from '../tool/normalizeSplineInterpolationPoints';
import {
    CLOSED_SPLINE_CURVE_SAMPLE_OPTIONS,
    type CurveSampleSegmentOptions,
    resolveCurveSampleSegments,
    SKETCH_DISPLAY_CURVE_SAMPLE_OPTIONS,
    SKETCH_PREVIEW_CURVE_SAMPLE_OPTIONS,
} from '../tool/resolveCurveSampleSegments';
import {isDashDotLIneNodeMaterial} from '../material';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../sketch/config/SketchGeometryToleranceConfig';


/** 样条预览颜色。 */
const SPLINE_PREVIEW_COLOR = 0x53a9ff;


/**
 * 样条绘制命令枚举。
 */
export enum SketchSplineCommand {
    /** 样条曲线命令。 */
    Spline = 'sketch-spline',
    /** 样式样条曲线命令。 */
    StyleSpline = 'sketch-style-spline',
}

/**
 * 判断命令是否为样条绘制命令。
 * @param command 命令字符串。
 * @returns 是否为样条绘制命令。
 */
export function isSketchSplineCommand(command: string): command is SketchSplineCommand {
    return Object.values(SketchSplineCommand).includes(command as SketchSplineCommand);
}

/**
 * 普通样条线绘制选项。
 */
export interface SplineOptions {
    /** 是否为构造几何。 */
    forConstruction: boolean;
    /** 是否显示曲率。 */
    showCurvature: boolean;
    /** 是否保持内部连续性。 */
    keepInternalContinuity: boolean;
}

/**
 * 草图样条族预览处理器。
 * 说明：
 * 1. 负责 `spline` / `bezier` 两类曲线预览；
 * 2. 统一处理异步预览调用。
 */
export class SketchSplineDrawingHandler extends SketchDrawingHandler {
    /** 当前处理器负责的主工具类型。 */
    readonly tool: SketchDrawingTool = SketchDrawingTool.Spline;

    /** 当前样条绘制流程中“已点击确认”的控制点预览对象列表。 */
    private readonly previewControlPointObjects: SketchPointObject[] = [];
    /** 当前样条绘制流程中跟随鼠标移动的临时控制点预览对象。 */
    private previewMovingControlPointObject: SketchPointObject | null = null;
    /** 当前是否正在执行样条完成流程。 */
    private isFinishingMultiPointDrawing: boolean = false;
    /** 每次样条绘制点击命中的捕捉目标点对象列表（与 drawingPoints 同步），用于实体化控制点时复用，确保选择链拓扑完整。 */
    private drawingPointTargets: Array<SketchPointObject | null> = [];

    /** 样条工具选项。 */
    public splineOptions: SplineOptions = {
        forConstruction: false,
        showCurvature: false,
        keepInternalContinuity: false,
    };

    /**
     * 构造函数。
     * @param sketchDrawingManager 草图绘制管理器实例。
     */
    constructor(sketchDrawingManager: SketchDrawingManager) {
        super(sketchDrawingManager);
    }

    getRequiredPointCount(): number {
        return Infinity;
    }

    
    get closable(): boolean {
        return true;
    }

    
    getDrawingHint(activeTool?: SketchDrawingTool): string {
        // 根据当前激活工具确定提示类型。
        void activeTool;
        const baseHint = '依次点击控制点，曲线穿过所有点';
        const pointCount = this.drawingPoints.length;
        const extraInfo = pointCount > 0 ? ` (${pointCount}个点)` : '';
        const actionHint = pointCount >= 2
            ? '双击/Enter完成 | Backspace撤销点 | ESC取消'
            : 'ESC取消';
        return `${baseHint}${extraInfo} | ${actionHint}`;
    }

    
    resetState(): void {
        this.drawingPoints = [];
        this.drawingPointTargets = [];
        this.isFinishingMultiPointDrawing = false;
        this.destroySplinePreviewCompletely();
    }

    /**
     * 处理样条族绘制鼠标按下事件。
     * @param payload 鼠标按下载荷。

     */
    handleMouseDown(payload: SketchDrawingMouseDownPayload,): void {
        const resolved = this.resolveMultiPointDrawingPoint(payload);
        if (!resolved) {
            console.log('无法获取样条绘制位置');
            return;
        }
        // 闭合/双击完成时提前返回，不追加绘制点，捕捉目标也不记录。
        if (this.handleMultiPointCommit(payload)) {
            return;
        }
        this.drawingPoints.push(resolved.clone());
        // 逐点记录捕捉目标端点，实体化控制点时逐点复用，确保选择链拓扑完整。
        this.drawingPointTargets.push(payload.snappedTargetPoint ?? null);
    }

    /**
     * 撤销样条绘制命令内尚未提交的最后一个输入点。
     * 说明：捕捉目标列表与绘制点一一对应，撤销点级输入时必须同步截断，避免残留引用错位。
     * @returns 是否真实撤销了一个未提交输入点。
     */
    public override undoLastDrawingPoint(): boolean {
        if (this.drawingPoints.length === 0) {
            return false;
        }
        this.drawingPoints.pop();
        this.drawingPointTargets.pop();
        return true;
    }

    /**
     * 统一预览更新入口。

     * @param currentPoint 当前预览点。
     * @param currentVersion 当前预览版本号。
     * @param tool 当前工具类型。
     */
    async updatePreview(
        currentPoint: THREE.Vector3,
        currentVersion: number,
        _tool?: SketchDrawingTool,
    ): Promise<void> {
        if (this.isFinishingMultiPointDrawing) {
            return;
        }
        await this.updateSplinePreviewByHandler(currentPoint, currentVersion);
    }


    /**
     * 更新样条预览。

     * @param currentPoint 当前预览点。
     * @param currentVersion 当前预览版本号。
     */
    async updateSplinePreviewByHandler(
        currentPoint: THREE.Vector3,
        currentVersion: number,
    ): Promise<void> {
        // 尚未放置第一个控制点时无需预览，静默返回。
        if (this.drawingPoints.length === 0) {
            super.destroyPreview();
            return;
        }
        const controlPoints = [...this.drawingPoints, currentPoint.clone()];

        const closureKind = this.resolvePreviewCurveClosureKind(controlPoints);

        const curvePoints = await this.computeWorkerCurvePoints(
            '2d_spline',
            controlPoints,
            undefined,
            SKETCH_PREVIEW_CURVE_SAMPLE_OPTIONS,
            closureKind,
        );
        if (currentVersion !== this.sketchDrawingManager.previewVersion) {
            return;
        }
        //------ 预览自相交冻结 开始------
        // 预览曲线自相交时整块冻结预览：不执行渲染写入，场景中保留上一次合法预览几何
        // （曲线预览线、控制点预览对象均不更新），直到鼠标移出自交区域恢复跟随。
        // 检测必须在基准面局部 2D 内进行：直接把世界坐标喂给检测等价于投影到世界 XY 平面，
        // 会让非 XY 平行的基准面（用户创建基准面、上视、右视等）在回画时误判自交并冻结预览。
        // closed 直接复用 Worker 采样使用的同一闭合判定，保证闭合样条接缝边对豁免。
        const isSelfIntersectingPreview = this.isPolylineSelfIntersectingInPlane(curvePoints, {
            closed: closureKind === SketchBezierCurveClosureKind.Closed,
        });
        if (isSelfIntersectingPreview) {
            return;
        }
        //------ 预览自相交冻结 结束------
        this.renderCurvePreviewInHandler({
            curvePoints,
            controlPoints,
            isConstruction: this.splineOptions.forConstruction,
            showControlPolyline: false,
        });
    }

    /**
     * 计算通用贝塞尔曲线上的点。
     * 使用 Bernstein 多项式: B(t) = Σ(i=0 to n) [C(n,i) * (1-t)^(n-i) * t^i * P_i]
     * @param t 参数 t，范围 [0, 1]。
     * @param controlPoints 控制点数组。
     * @returns 贝塞尔曲线上的点。
     */
    public bezierPoint(t: number, controlPoints: THREE.Vector3[]): THREE.Vector3 {
        // 贝塞尔曲线的阶数。
        const n = controlPoints.length - 1;
        const result = new THREE.Vector3(0, 0, 0);

        for (let i = 0; i <= n; i++) {
            // 计算 Bernstein 基函数值。
            const coefficient = this.binomialCoefficient(n, i);
            const blend = coefficient * Math.pow(1 - t, n - i) * Math.pow(t, i);
            result.x += blend * controlPoints[i].x;
            result.y += blend * controlPoints[i].y;
            result.z += blend * controlPoints[i].z;
        }

        return result;
    }


    /**
     * 请求 Worker 计算曲线采样点。
     * @param curveType 曲线类型。
     * @param controlPoints 控制点列表。
     * @param degree B 样条阶数。
     * @param sampleOptions 采样配置。
     * @param closureKind 普通样条闭合状态。
     * @param onResult 可选 Worker 原始结果接收器，用于实体提交时保留权威曲线定义。
     * @returns 曲线点列表；Worker 失败时抛错。
     */
    private async computeWorkerCurvePoints(
        curveType: '2d_spline' | '2d_bezier' | '2d_bspline',
        controlPoints: THREE.Vector3[],
        degree?: number,
        sampleOptions?: CurveSampleSegmentOptions,
        closureKind?: SketchBezierCurveClosureKind,
        onResult?: (result: Compute2DCurveResponse) => void,
    ): Promise<THREE.Vector3[]> {
        const normalizedControlPoints = curveType === '2d_spline'
            ? normalizeSplineInterpolationPoints(controlPoints)
            : [...controlPoints];
        const segments = resolveCurveSampleSegments(normalizedControlPoints, sampleOptions);
        const result = await this.viewer.workerManager.compute2DCurve({
            curveType,
            params: {
                points: normalizedControlPoints.map((point) => ({x: point.x, y: point.y, z: point.z})),
                segments,
                degree,
                closureKind,
            },
        });
        onResult?.(result);
        return result.points.map((point) => new THREE.Vector3(point.x, point.y, point.z));
    }


    /**
     * 解析预览曲线闭合状态。
     * 说明：
     * 1. 预览阶段没有鼠标按下载荷，无法读取 `snappedTargetPoint`；
     * 2. 这里只在首尾已实际重合且具备三个唯一样条点时启用闭合周期插值；
     * 3. 普通开放预览继续使用 Puppy CAD 内核 开放插值，避免改变非闭合绘制手感。
     * @param controlPoints 当前预览控制点。
     * @returns 预览闭合状态。
     */
    private resolvePreviewCurveClosureKind(controlPoints: THREE.Vector3[]): SketchBezierCurveClosureKind {
        if (
            controlPoints.length >= 4
            && this.isClosedCurve(controlPoints[0]!, controlPoints[controlPoints.length - 1]!)
        ) {
            return SketchBezierCurveClosureKind.Closed;
        }
        return SketchBezierCurveClosureKind.Open;
    }


    /**
     * 在处理器内渲染曲线预览。
     * @param options 预览渲染选项。
     */
    private renderCurvePreviewInHandler(options: {
        /** 曲线采样点列表。 */
        curvePoints: THREE.Vector3[];
        /** 控制点列表。 */
        controlPoints: THREE.Vector3[];
        /** 是否构造线。 */
        isConstruction: boolean;
        /** 是否显示控制折线。 */
        showControlPolyline: boolean;
    }): void {
        // 预览刷新时只清理线型预览，保留控制点对象并复用，避免每次鼠标移动都重新创建点对象。
        super.destroyPreview();
        if (options.curvePoints.length < 2) {
            return;
        }
        if (options.showControlPolyline) {
            const controlGeometry = new THREE.BufferGeometry().setFromPoints(options.controlPoints);
            const controlMaterial = new THREE.LineDashedMaterial({
                color: SPLINE_PREVIEW_COLOR,
                linewidth: 1,
                dashSize: 3,
                gapSize: 2,
                opacity: 0.4,
                transparent: true,
            });
            this.sketchDrawingManager.previewLine = new THREE.Line(controlGeometry, controlMaterial);
            this.sketchDrawingManager.previewLine.computeLineDistances();
        this.viewer.addObject(this.sketchDrawingManager.previewLine);
        }

        const curveGeometry = new THREE.BufferGeometry().setFromPoints(options.curvePoints);
        const curveMaterial = options.isConstruction
            ? new THREE.LineDashedMaterial({
                color: SPLINE_PREVIEW_COLOR,
                linewidth: 1,
                dashSize: 3,
                gapSize: 2,
                opacity: 0.8,
                transparent: true,
            })
            : new THREE.LineBasicMaterial({
                color: SPLINE_PREVIEW_COLOR,
                linewidth: 2,
                opacity: 0.8,
                transparent: true,
            });
        const targetPreviewKey = options.showControlPolyline ? 'previewExtraLine' : 'previewLine';
        this.sketchDrawingManager[targetPreviewKey] = new THREE.Line(curveGeometry, curveMaterial);
        if (curveMaterial instanceof THREE.LineDashedMaterial) {
            this.sketchDrawingManager[targetPreviewKey].computeLineDistances();
        }
        this.viewer.addObject(this.sketchDrawingManager[targetPreviewKey]);
        this.syncPreviewControlPointObjects(options.controlPoints);
    }


    /**
     * 同步样条绘制过程中的临时控制点预览对象。
     * 说明：
     * 1. 已点击确认的控制点只在数量增加时创建一次；
     * 2. 跟随鼠标移动的控制点单独复用一个对象；
     * 3. 点位变化时仅更新几何，不进入正式草图实体集合。
     * @param controlPoints 当前应显示的控制点世界坐标列表。
     */
    private syncPreviewControlPointObjects(controlPoints: THREE.Vector3[]): void {
        const fixedControlPointCount = Math.max(controlPoints.length - 1, 0);

        while (this.previewControlPointObjects.length < fixedControlPointCount) {
            const previewPoint = this.createLineControlPoint(controlPoints[this.previewControlPointObjects.length]);

            // 样条绘制过程中的预览控制点仅用于预览，禁止进入正式选择流程。
            previewPoint.isDrawingPreviewPoint = true;

            this.previewControlPointObjects.push(previewPoint);
        }

        this.previewControlPointObjects.forEach((previewPoint, index) => {
            if (index < fixedControlPointCount) {
                if (previewPoint.parent !== this.viewer.scene) {
        this.viewer.addObject(previewPoint);
                }
                previewPoint.visible = true;
                previewPoint.syncPositionSilently(controlPoints[index]);
                return;
            }
            if (previewPoint.parent === this.viewer.scene) {
                this.viewer.scene.remove(previewPoint);
            }
            previewPoint.visible = false;
        });

        const movingControlPoint = controlPoints.at(-1) ?? null;
        if (!movingControlPoint) {
            if (this.previewMovingControlPointObject) {
                if (this.previewMovingControlPointObject.parent === this.viewer.scene) {
                    this.viewer.scene.remove(this.previewMovingControlPointObject);
                }
                this.previewMovingControlPointObject.visible = false;
            }
            return;
        }

        if (!this.previewMovingControlPointObject) {
            this.previewMovingControlPointObject = this.createLineControlPoint(movingControlPoint);

            // 跟随鼠标的临时控制点不参与 hover / click 选择。
            this.previewMovingControlPointObject.isDrawingPreviewPoint = true;

        }
        if (this.previewMovingControlPointObject.parent !== this.viewer.scene) {
        this.viewer.addObject(this.previewMovingControlPointObject);
        }
        this.previewMovingControlPointObject.visible = true;
        this.previewMovingControlPointObject.syncPositionSilently(movingControlPoint);
    }

    /**
     * 销毁当前样条控制点预览对象。
     */
    private disposePreviewControlPoints(): void {
        if (!this.viewer.scene) {
            this.previewControlPointObjects.forEach((previewPoint) => {
                previewPoint.visible = false;
            });
            if (this.previewMovingControlPointObject) {
                this.previewMovingControlPointObject.visible = false;
            }
            return;
        }

        this.previewControlPointObjects.forEach((previewPoint) => {
            if (previewPoint.parent === this.viewer.scene) {
                this.viewer.scene.remove(previewPoint);
            }
            previewPoint.visible = false;
        });

        if (this.previewMovingControlPointObject) {
            if (this.previewMovingControlPointObject.parent === this.viewer.scene) {
                this.viewer.scene.remove(this.previewMovingControlPointObject);
            }
            this.previewMovingControlPointObject.visible = false;
        }
    }

    /**
     * 完整销毁样条预览。
     * 说明：
     * 1. 线型预览与控制点预览都在此处清理；
     * 2. 仅在结束绘制、重置状态等真正需要退出样条预览时调用。
     */
    private destroySplinePreviewCompletely(): void {
        super.destroyPreview();
        this.disposePreviewControlPoints();
    }


    /**
     * 获取当前绘制流程允许参与点捕捉的临时首点。
     * 说明：
     * 1. 仅在已经具备闭合条件时开放首点捕捉；
     * 2. 其它控制点继续保持为纯预览对象，不参与点捕捉；
     * 3. 返回对象来自预览控制点，不进入正式草图组集合。
     * @returns 当前允许参与捕捉的首点预览对象；不存在时返回 null。
     */
    public getTransientSnappableStartPoint(): SketchPointObject | null {
        if (!this.canSnapToClosureStart()) {
            return null;
        }
        return this.previewControlPointObjects[0] ?? null;
    }

    /**
     * 解析样条族绘制点。
     * 说明：当当前点击明确吸附到链路起点预览对象时，吸附到起点以便闭合。
     * @param payload 鼠标按下载荷。
     * @returns 解析后的绘制点；无法获取时返回 null。
     */
    private resolveMultiPointDrawingPoint(payload: SketchDrawingMouseDownPayload): THREE.Vector3 | null {
        const point = payload.snappedPoint ?? this.getDrawingPoint(payload.event);
        if (!point) {
            return null;
        }


        // 只有明确捕捉到样条首点预览对象时才替换为链路起点，避免实体面上普通第三点被距离容差误判为闭合。
        if (this.isSnappedToClosureStart(payload)) {
            const chainStartPoint = this.resolveChainStartPointForClosure();
            if (chainStartPoint) {
                return chainStartPoint.clone();
            }
        }


        return point.clone();
    }


    /**
     * 判断当前点击是否明确吸附到样条链路起点。
     * @param payload 鼠标按下载荷。
     * @returns 是否命中样条首点预览对象。
     */
    private isSnappedToClosureStart(payload: SketchDrawingMouseDownPayload): boolean {
        const transientStartPoint = this.getTransientSnappableStartPoint();

        // 以捕捉目标对象身份作为闭合意图，避免仅凭坐标距离导致特征面第三次点击被误闭合。
        return transientStartPoint !== null && payload.snappedTargetPoint === transientStartPoint;
    }


    /**
     * 处理样条族多点提交与闭合。
     * 说明：
     * 1. 闭合到链路起点：当前点击明确吸附到链路首点时闭合并完成；
     * 2. 双击检测：浏览器原生双击视为完成；
     * 3. 其余情况返回 false，由调用方继续追加控制点。
     * @param payload 鼠标按下载荷。
     * @returns 是否已提交（true 表示已完成绘制，调用方不应再追加点）。
     */
    private handleMultiPointCommit(payload: SketchDrawingMouseDownPayload): boolean {
        const chainStartPoint = this.resolveChainStartPointForClosure();


        // 闭合到链路起点：仅当当前点击明确吸附到样条首点预览对象时闭合。
        if (this.drawingPoints.length >= 2 && chainStartPoint && this.isSnappedToClosureStart(payload)) {
            this.drawingPoints.push(chainStartPoint.clone());
            this.scheduleFinishMultiPointDrawing();
            return true;
        }


        // 双击检测：必须来自浏览器原生双击计数，避免第三次普通点击因时间间隔短被误判结束。
        // 闭合完成后的第二击（isFinishingMultiPointDrawing 已置位）不得再次进入双击完成流程。
        if (payload.event.detail >= 2 && this.drawingPoints.length >= 2 && !this.isFinishingMultiPointDrawing) {
            //------ 双击控制点收口到预览模型 开始------
            // 浏览器原生双击由两次 mousedown 组成：第一击已把双击位置作为控制点 push 进 drawingPoints。
            // 这里把该临时落点替换为预览模型最后控制点（previewMovingControlPointObject 位置），
            // 保证最终生成曲线与预览模型一致：正常流下两位置重合（pop+push 无变化，不丢点），
            // 预览冻结/渲染延迟时收口到最后一次合法预览曲线。
            const previewLastControlPoint = this.previewMovingControlPointObject;
            if (previewLastControlPoint) {
                // 双击第一击已把末点坐标与吸附目标 push 进列表，位置回贴只替换坐标；
                // 必须保留原吸附目标，否则末点退化为新建点、与已有端点拓扑断开。
                const lastTarget = this.drawingPointTargets[this.drawingPointTargets.length - 1] ?? null;
                this.drawingPoints.pop();
                this.drawingPointTargets.pop();
                this.drawingPoints.push(previewLastControlPoint.getPosition().clone());
                this.drawingPointTargets.push(lastTarget);
            }
            this.scheduleFinishMultiPointDrawing();
            return true;
        }


        this.sketchDrawingManager.setLastLineClickTimestamp(Date.now());
        return false;
    }

    /**
     * 计算二项式系数 C(n, k)。
     * @param n 总数。
     * @param k 选择数。
     * @returns 二项式系数值。
     */
    private binomialCoefficient(n: number, k: number): number {
        if (k < 0 || k > n) return 0;
        if (k === 0 || k === n) return 1;

        let result = 1;
        for (let i = 1; i <= k; i++) {
            result *= (n - i + 1) / i;
        }
        return result;
    }

    /**
     * 判断曲线起终点是否封闭。
     * @param start 起点。
     * @param end 终点。
     * @param epsilon 距离容差，默认读取草图几何容差配置。
     * @returns 起终点距离小于容差时返回 true。
     */
    private isClosedCurve(
        start: THREE.Vector3,
        end: THREE.Vector3,
        epsilon = SKETCH_GEOMETRY_TOLERANCE_CONFIG.closedCurvePosition,
    ): boolean {
        return start.distanceTo(end) < epsilon;
    }

    /**
     * 完成多点（样条族）绘图。
     * 说明：
     * 1. 负责检查点数是否足够；
     * 2. 根据当前工具类型调用对应的实体创建方法；
     * 3. 清除预览，并根据 stopDrawing 选项决定是否退出绘图模式。
     * @param options 完成选项。
     */
    public async finishMultiPointDrawing(options?: { stopDrawing?: boolean }): Promise<void> {
        if (this.isFinishingMultiPointDrawing) {
            return;
        }
        const tool = this.sketchDrawingManager.getCurrentTool();

        // 检查点数是否足够
        const minPoints = 2;
        if (this.drawingPoints.length < minPoints) {
            console.log(`${String(tool)} 至少需要${minPoints}个点才能完成`);
            return;
        }

        this.isFinishingMultiPointDrawing = true;
        try {
            await this.sketchDrawingManager.executeActiveSketchRuntimeHistorySession({
                label: `草图绘制：${SketchDrawingTool.Spline}`,
                commandType: `sketch.runtime.draw.${SketchDrawingTool.Spline}`,
                work: async () => {
                    await this.finishMultiPointDrawingCore(options);
                },
            });
        } finally {
            this.isFinishingMultiPointDrawing = false;
        }
    }

    /**
     * 执行样条实体创建与绘制状态清理。
     *
     * 说明：不定点工具的普通落点不进入历史；只有完成时才捕获整组草图 before/after，
     * 避免首点、预览点克隆整组草图造成交互阻塞。
     *
     * @param options 完成选项。
     */
    private async finishMultiPointDrawingCore(options?: { stopDrawing?: boolean }): Promise<void> {
        const tool = this.sketchDrawingManager.getCurrentTool();

        // 双击完成时先作废所有在途预览任务，避免旧 Worker 回调继续销毁/重建预览对象。
        this.sketchDrawingManager.invalidatePreviewVersion();
        if (tool === SketchDrawingTool.Spline) {
            await this.createSplineEntityByHandler();
        }

        this.destroySplinePreviewCompletely();
        const stopDrawing = options?.stopDrawing ?? false;
        if (stopDrawing) {
            this.sketchDrawingManager.stopDrawing();
            return;
        }
        this.drawingPoints = [];
        // 绘制点清空时同步清空捕捉目标列表，避免残留引用影响下一次绘制。
        this.drawingPointTargets = [];
        this.sketchDrawingManager.setPreviewPoint(null);
        this.sketchDrawingManager.setLastLineClickTimestamp(0);
        this.viewer.lastClickTime = 0;
    }

    /**
     * 调度样条完成提交并集中处理异步失败。
     *
     * 说明：双击/闭合完成发生在同步 mouseDown 链路内，不能 await；
     * 这里必须显式 catch，避免历史提交失败变成不可定位的未处理 Promise。
     */
    private scheduleFinishMultiPointDrawing(): void {
        void this.finishMultiPointDrawing().catch((error) => {
            console.error('样条绘制提交失败', error);
            this.viewer.drawingNoticeCallback?.('样条绘制提交失败，请重试。', 'error');
        });
    }

    /**
     * 基于指定控制点与采样点创建统一曲线草图对象。
     * @param options 曲线草图对象创建参数。
     * @returns 曲线草图对象。
     */
    private createCurveSketchObject(options: {
        /** Worker 返回的采样曲线点列表。 */
        curvePoints: THREE.Vector3[];
        /** 是否为构造线。 */
        isConstruction: boolean;
        /** 应实体化的控制点位置列表。 */
        controlPointPositions: THREE.Vector3[];
        /** 曲线对象构造类型。 */
        curveObjectClass: typeof SketchSplineObject;
        /** 曲线元数据。 */
        curveMetadata: SketchBezierCurveMetadata;
        /** Worker 导出的权威 B 样条求值定义。 */
        curveEvaluationDefinition?: SketchBSplineEvaluationDefinition;
    }): SketchSplineObject {
        if (options.curvePoints.length < 2) {
            throw new Error('曲线采样点数量不足，无法创建草图对象。');
        }

        // 解析与实体控制点位置一一对应的可复用捕捉目标，供控制点实体化时逐点复用。
        const controlPointTargets = this.resolveReusableControlPointTargets(
            options.controlPointPositions,
            this.drawingPointTargets,
        );
        // 为每一个绘制点创建对应的草图控制点，保持交互语义与绘制输入一致。
        const pointObjects = this.createCurveControlPointObjects(options.controlPointPositions, controlPointTargets);
        // 使用统一草图线材质工厂，确保贝塞尔对象与其他草图边对象的视觉状态一致。
        const material = this.createSketchLineMaterial(options.isConstruction);
        const bezierObject = new options.curveObjectClass({
            startPoint: pointObjects.startPoint,
            endPoint: pointObjects.endPoint,
            controlPoints: pointObjects.controlPoints,
            curvePoints: options.curvePoints,
            material,
            referencePlane: this.referencePlane,
            isConstruction: options.isConstruction,
            curveMetadata: options.curveMetadata,
            curveEvaluationDefinition: options.curveEvaluationDefinition,
        });

        if (material instanceof THREE.LineDashedMaterial || isDashDotLIneNodeMaterial(material)) {
            bezierObject.computeLineDistances();
        }

        // 同步实体与控制点的外部材质快照，确保交互状态系统接管时视觉一致。
        this.syncCurveMaterialSnapshots(bezierObject, pointObjects.allPoints);
        return bezierObject;
    }

    /**
     * 解析曲线实体应使用的控制点位置列表。
     * 说明：
     * 1. 闭合曲线的绘制输入会额外追加一次首点；
     * 2. 实体化控制点时只保留一份首点，避免生成重复端点控制点。
     * @param curvePoints Worker 返回的采样曲线点列表。
     * @returns 去重后的控制点位置列表。
     */
    private resolveEntityControlPointPositions(curvePoints: THREE.Vector3[]): THREE.Vector3[] {
        const normalizedDrawingPoints = normalizeSplineInterpolationPoints(this.drawingPoints);
        if (normalizedDrawingPoints.length < 2) {
            throw new Error('曲线控制点数量不足，无法解析控制点位置。');
        }
        const isClosed = this.isClosedCurve(curvePoints[0], curvePoints[curvePoints.length - 1]);
        if (!isClosed) {
            return normalizedDrawingPoints.map((point) => point.clone());
        }
        return normalizedDrawingPoints.slice(0, -1).map((point) => point.clone());
    }

    /**
     * 为当前曲线绘制流程创建控制点对象集合。
     * @param controlPointPositions 需要实体化的控制点位置列表。
     * @param controlPointTargets 与位置列表等长的可复用捕捉目标列表；对应项非空时直接复用该点对象，否则新建。
     * @returns 曲线首点、尾点和中间控制点对象。
     */
    private createCurveControlPointObjects(
        controlPointPositions: THREE.Vector3[],
        controlPointTargets: readonly (SketchPointObject | null)[],
    ): {
        /** 起点控制点。 */
        startPoint: SketchPointObject;
        /** 终点控制点。 */
        endPoint: SketchPointObject;
        /** 中间控制点列表。 */
        controlPoints: SketchPointObject[];
        /** 全部控制点列表。 */
        allPoints: SketchPointObject[];
    } {
        if (controlPointPositions.length < 2) {
            throw new Error('曲线控制点数量不足，无法创建控制点对象。');
        }

        // 逐个控制点尝试复用吸附命中的已有端点：命中则直接共享点对象，未命中才新建，确保端点对象共享以支持选择链。
        const allPoints: SketchPointObject[] = controlPointPositions.map((position, index) => {
            const reusablePoint = controlPointTargets[index];
            return reusablePoint ?? this.createLineControlPoint(position);
        });

        const startPoint = allPoints[0]!;
        const endPoint = allPoints[allPoints.length - 1]!;
        const controlPoints = allPoints.slice(1, -1);
        return {
            startPoint,
            endPoint,
            controlPoints,
            allPoints,
        };
    }

    /**
     * 同步曲线对象及其控制点的材质快照。
     * @param bezierObject 曲线草图对象。
     * @param controlPoints 控制点对象列表。
     */
    private syncCurveMaterialSnapshots(
        bezierObject: SketchSplineObject,
        controlPoints: SketchPointObject[],
    ): void {
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(bezierObject);
        controlPoints.forEach((point) => {
            this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(point);
        });
    }

    /**
     * 基于曲线元数据计算采样点。
     * @param controlPointPositions 控制点世界坐标列表。
     * @param curveMetadata 曲线元数据。
     * @returns 新采样曲线点列表。
     */
    private async computeCurvePointsByMetadata(
        controlPointPositions: THREE.Vector3[],
        curveMetadata: SketchBezierCurveMetadata,
    ): Promise<THREE.Vector3[]> {
        const normalizedControlPointPositions = curveMetadata.curveKind === SketchBezierCurveKind.Spline
            ? normalizeSplineInterpolationPoints(controlPointPositions).map((point) => point.clone())
            : controlPointPositions;
        const sampleOptions = curveMetadata.closureKind === SketchBezierCurveClosureKind.Closed
            ? CLOSED_SPLINE_CURVE_SAMPLE_OPTIONS
            : SKETCH_DISPLAY_CURVE_SAMPLE_OPTIONS;
        if (curveMetadata.curveKind === SketchBezierCurveKind.Spline) {
            return this.computeWorkerCurvePoints(
                '2d_spline',
                normalizedControlPointPositions,
                undefined,
                sampleOptions,
                curveMetadata.closureKind,
            );
        }
        if (curveMetadata.curveKind === SketchBezierCurveKind.BSpline) {
            const segments = resolveCurveSampleSegments(normalizedControlPointPositions, sampleOptions);
            return this.viewer.workerManager.compute2DCurve({
                curveType: '2d_bspline',
                params: {
                    points: normalizedControlPointPositions.map((point) => ({x: point.x, y: point.y, z: point.z})),
                    segments,
                    degree: curveMetadata.degree ?? 3,
                    weights: curveMetadata.isRational ? curveMetadata.weights : undefined,
                },
            }).then((result) => result.points.map((point) => new THREE.Vector3(point.x, point.y, point.z)));
        }
        const segments = resolveCurveSampleSegments(normalizedControlPointPositions, sampleOptions);
        return this.viewer.workerManager.compute2DCurve({
            curveType: '2d_bezier',
            params: {
                points: normalizedControlPointPositions.map((point) => ({x: point.x, y: point.y, z: point.z})),
                segments,
                weights: curveMetadata.isRational ? curveMetadata.weights : undefined,
            },
        }).then((result) => result.points.map((point) => new THREE.Vector3(point.x, point.y, point.z)));
    }


    /**
     * 创建样条曲线实体（穿过所有控制点，使用 Puppy CAD 内核 计算）。
     */
    private async createSplineEntityByHandler(): Promise<void> {
        const normalizedDrawingPoints = normalizeSplineInterpolationPoints(this.drawingPoints);
        if (normalizedDrawingPoints.length < 2 ) return;
        try {
            let curveEvaluationDefinition: SketchBSplineEvaluationDefinition | undefined;
            const isClosedInput = this.isClosedCurve(
                normalizedDrawingPoints[0]!,
                normalizedDrawingPoints[normalizedDrawingPoints.length - 1]!,
            );
            // 将绘制点转换为坐标对象数组
            const points = await this.computeWorkerCurvePoints(
                '2d_spline',
                normalizedDrawingPoints,
                undefined,
                isClosedInput ? CLOSED_SPLINE_CURVE_SAMPLE_OPTIONS : SKETCH_DISPLAY_CURVE_SAMPLE_OPTIONS,
                isClosedInput ? SketchBezierCurveClosureKind.Closed : SketchBezierCurveClosureKind.Open,
                (result) => {
                    curveEvaluationDefinition = result.evaluationDefinition;
                },
            );

            // 第四点回捕首点后，Worker 采样首尾会重合，此处统一把它识别为自闭合样条。
            const isSelfClosedSpline = this.isClosedCurve(points[0], points[points.length - 1]);

            const controlPointPositions = this.resolveEntityControlPointPositions(points);
            const splineObject = this.createCurveSketchObject({
                curvePoints: points,
                isConstruction: this.splineOptions.forConstruction,
                controlPointPositions,
                curveObjectClass: SketchSplineObject,
                curveMetadata: {
                    selectionKind: SketchBezierSelectionKind.Spline,
                    curveKind: SketchBezierCurveKind.Spline,

                    closureKind: isSelfClosedSpline
                        ? SketchBezierCurveClosureKind.Closed
                        : SketchBezierCurveClosureKind.Open,

                    styleSplineMode: null,
                    degree: 3,
                    weights: new Array(controlPointPositions.length).fill(1),
                    isRational: false,
                },
                curveEvaluationDefinition,
            });
            if (this.viewer.shadedSketchContoursEnabled && splineObject.isClosedGeometry) {
                splineObject.syncContour();
            }
            this.viewer.addSketchObject(splineObject);
            this.sketchDrawingManager.notifySketchEntityCreated(splineObject);
            if (!isSelfClosedSpline) {
                this.tryFinalizeMixedClosedCollection([splineObject]);
            }
            const sketchGroupManager = this.viewer.sketchGroupManager;
            if (!sketchGroupManager) {
                throw new Error('草图组管理器未初始化，无法同步样条闭合后的轮廓显示状态。');
            }
            this.viewer.syncShadedContoursAfterSketchEntityCreated(sketchGroupManager.activeSketchGroup);
        } catch (error) {
            throw error;
        }
    }

}


